import React, { useState, useEffect, useContext } from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { UserContext } from '../../contexts/UserContext';

import ManageUserForm from '../CMSForm/ManageUserForm';
import OperatorActionErrorModal from '../CMSForm/OperatorActionErrorModal';

import './ManageFacilities.css';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

function ManageUsers() {
  const { getAllUsers, allUsers, getUserCount, userCount, page, setPage, deleteUser, unviewedUsers, markUserAsViewed, markAllUsersAsViewed } = useContext(UserContext)

  const [hidden, setHidden] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [errorModal, setErrorModal] = useState(null)
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null)

  const [searchString, setSearchString] = useState('')
  const [filteredData, setFilteredData] = useState([])
  const [editFormData, setEditFormData] = useState({})

  function toggleForm() {
    setHidden(!hidden)
  }

  useEffect(() => {
    getUserCount()
  }, [])

  useEffect(() => {
    getAllUsers(page, searchString)
  }, [page])


  useEffect(() => {
    setFilteredData([...processData(allUsers)])
  }, [allUsers])


  function processData(data) {
    const newData = data.map(e => createData(e.first_name, e.last_name, e.email, e.position, e.shortDescription, e.create_date, e.organizations?.length > 0, !!e.site_admin))
    return newData
  }

  function createData(first_name, last_name, email, position, shortDescription, create_date, hasOrganization, site_admin) {
    return { first_name, last_name, email, position, shortDescription, create_date, hasOrganization, site_admin };
  }

  function handleSearch(e) {
    let string = e.target.value
    setSearchString(string)
    setPage(1)

    if (string !== '') {
      getAllUsers(1, string)
      getUserCount(string)
    }
    else {
      getAllUsers(1)
      getUserCount()
    }
  }

  function handleCreateNew() {
    setEditFormData({})
    toggleForm()
  }

  function handleEditClick(i) {
    let selected = getCurrListData(i)
    // Mark user as viewed when opened for editing
    if (selected) {
      markUserAsViewed(selected._id)
      setEditFormData({ ...selected })
      toggleForm()
    }
  }

  function getCurrListData(i) {
    const row = filteredData[i];
    return allUsers.find(user => user.email === row.email);
  }

  async function handleDelete(additionalFlags = {}) {
    try {
      // Use stored selection if retrying, otherwise get current selection
      let selected = selectedUserForDelete || getCurrListData(deleteConfirm)
      if (selected) {
        // Store the selection for potential retries
        if (!selectedUserForDelete) {
          setSelectedUserForDelete(selected)
        }
        await deleteUser(selected._id, searchString, additionalFlags)
        setDeleteConfirm(null)
        setErrorModal(null)
        setSelectedUserForDelete(null)
      }
    } catch (error) {
      console.log(error)
      // Check if error response contains our specific error codes
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.error === 'organization_has_active_subscriptions' ||
            errorData.error === 'operator_has_active_subscriptions' ||
            errorData.error === 'require_move_and_delete_organization' ||
            errorData.error === 'organization_has_assigned_users' ||
            errorData.error === 'operator_has_assigned_users') {
          setDeleteConfirm(null); // Close the confirmation dialog
          setErrorModal(errorData);
          return;
        }
      }
      // For other errors, close confirmation dialog and re-throw
      setDeleteConfirm(null);
      setSelectedUserForDelete(null);
      throw error;
    }
  }

  const handleMoveAndDeleteOperator = () => {
    handleDelete({ move_and_delete_organization: true });
  };

  const handleRetry = handleMoveAndDeleteOperator;

  const handleRetryWithCancelSubscription = () => {
    handleDelete({ cancel_active_subscription: true, move_and_delete_organization: true });
  };

  const handleCancelErrorModal = () => {
    setErrorModal(null);
    setSelectedUserForDelete(null);
  };

  return (
    <div>
      <OperatorActionErrorModal
        error={errorModal}
        onRetry={handleRetry}
        onCancel={handleCancelErrorModal}
        onRetryWithCancelSubscription={handleRetryWithCancelSubscription}
        onMoveAndDeleteOperator={handleMoveAndDeleteOperator}
      />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar style={{ backgroundColor: 'white' }} position="static">
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }} >
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search…"
                inputProps={{ 'aria-label': 'search' }}
                onChange={(e) => { handleSearch(e) }}
                value={searchString}
              />
            </Search>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {unviewedUsers.length > 0 && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={markAllUsersAsViewed}
                  style={{ marginRight: '16px' }}
                >
                  Mark All as Viewed ({unviewedUsers.length})
                </Button>
              )}
              <Button onClick={handleCreateNew} variant="contained">Create New</Button>
            </div>
          </Toolbar>
        </AppBar>
      </Box>

      {/* form */}
      {hidden && <ManageUserForm
        initialData={editFormData}
        closeForm={() => { toggleForm() }}
        search={searchString}
      />}


      <TableContainer style={{ marginTop: '30px' }} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Create Date</TableCell>
              <TableCell>Organization User</TableCell>
              <TableCell>Site Admin</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row, i) => {
              const currentUser = allUsers.find(user => user.email === row.email);
              const isUnviewed = currentUser ? unviewedUsers.some(user => user._id === currentUser._id) : false;
              
              return (
                <TableRow
                  key={row.email + i}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: isUnviewed ? '#fff5f5' : 'inherit'
                  }}
                >
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {isUnviewed && (
                        <div 
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#f44336',
                            marginRight: '8px',
                            flexShrink: 0
                          }}
                        />
                      )}
                      {row.first_name}
                    </div>
                  </TableCell>
                  <TableCell>{row.last_name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.position || '—'}</TableCell>
                  <TableCell>{new Date(row.create_date).toLocaleDateString()}</TableCell>
                  <TableCell>{row.hasOrganization ? "True" : "False"}</TableCell>
                  <TableCell>{row.site_admin ? "True" : "False"}</TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <TableOption
                        color={'primary'}
                        name={'Edit'}
                        onClick={() => { handleEditClick(i) }}
                      >
                        <EditIcon />
                      </TableOption>
                      <TableOption
                        color={'primary'}
                        name={'Delete'}
                        onClick={() => { setDeleteConfirm(i) }}
                      >
                        <DeleteIcon />
                      </TableOption>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteConfirm !== null}
        onClose={() => { setDeleteConfirm(null) }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Delete User and Review Organization Impact
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Are you sure you want to delete ${filteredData[deleteConfirm] ? filteredData[deleteConfirm].email : ''}? If this user is the last user for this organization, you will be asked to move the user to the new organization and the current organization will be deleted. If this organization has active subscriptions that need to be handled, you will be asked to Retry and Cancel Subscription, which cancels the subscription and moves the user. If this organization has assigned users that must be handled before deletion, please reassign or remove the users first.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirm(null) }}>Cancel</Button>
          <Button onClick={() => { handleDelete() }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {userCount > 15 &&
        <div style={{ width: '100%', margin: '40px 0px', display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={2}>
            <Pagination page={page} onChange={(e, i) => { setPage(i) }} count={Math.ceil(userCount / 15)} showFirstButton={userCount / 15 > 5 ? true : false} showLastButton={userCount / 15 > 5 ? true : false} />
          </Stack>
        </div>
      }
    </div>
  )
}

export default ManageUsers

const TableOption = ({ children, name, color, onClick }) => {
  return (
    <div style={{ margin: '0px 10px', cursor: 'pointer' }} onClick={onClick}>
      <Tooltip title={name}>
        <IconButton color={color}>
          {children}
        </IconButton>
      </Tooltip>
    </div>
  )
}